import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { toast } from 'react-toastify';
import { DesignSystemComponent, ComponentProp } from 'designSystem/types';
import Pill from 'components/Widget/Pill';
import { Tooltip } from 'components/Widget/Tooltip';
import { symbolDisplayName } from 'designSystem/content/util';
import { ChatMode, ChatStatus } from 'dragonmouse-shared/interfaces';
import * as api from 'dragonmouse-shared/api/forge';
import forge from 'apis/forge';
import { RootReducer } from 'redux/reducers';
import useScrollMemory from 'hooks/useScrollMemory';
import ComponentPreview from '../ComponentPreview';
import {
	addDsTab,
	removeDsTab,
	removeDsPick,
	resolveBreakageSymbols,
	setActiveDsTab,
	showContextMenu,
	toggleDsPick,
} from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import { DS_TAB_CHROME } from 'designSystem/config';
import { MenuItemAction } from 'menu/MenuItem';
import Icon, { IconType } from 'components/Icon';
import IconNameInput from 'components/Widget/Input/IconNameInput';
import { DEFAULT_ICON_PACK } from 'dragonmouse-shared/constants';
import MarkdownEditor from 'components/Widget/Input/MarkdownEditor';
import { designSystemEditingBlocked } from 'redux/db/util';
import * as S from './styles';

const SAVE_DEBOUNCE_MS = 1500;

interface ComponentPageProps {
	component: DesignSystemComponent;
	rawCode: {
		theme: string;
		styles: string;
		componentsCss: string;
		componentsJs: string;
	};
	showBackButton?: boolean;
}

// Builds example HTML from component props and children (mirrors ComponentPreview serialization)
const buildExampleHtml = (
	component: DesignSystemComponent,
	props: Record<string, any>,
	children: string
): string => {
	const attributes = Object.entries(props)
		.filter(([, value]) => value !== undefined && value !== null && value !== '')
		.map(([key, value]) => {
			const prop = component.props.find((p) => p.name === key);
			if (prop?.type === 'json') {
				try {
					const jsonStr = typeof value === 'string' ? value : JSON.stringify(value);
					return `${key}='${jsonStr.replace(/'/g, '&#39;')}'`;
				} catch {
					return '';
				}
			} else if (prop?.type === 'boolean') {
				return value ? key : '';
			} else {
				return `${key}="${String(value).replace(/"/g, '&quot;')}"`;
			}
		})
		.filter(Boolean)
		.join(' ');
	const attrsStr = attributes ? ' ' + attributes : '';
	return `<${component.tagName}${attrsStr}>${children}</${component.tagName}>`;
};

// Serialize a value to display text for the JSON textarea
const serializeJsonValue = (value: any, fallback: string) => {
	if (value === undefined || value === null) return fallback;
	if (typeof value === 'string') return value;
	return JSON.stringify(value, null, 2);
};

// JSON input component with error state
const JsonInput = ({
	prop,
	value,
	onChange,
}: {
	prop: ComponentProp;
	value: any;
	onChange: (v: any) => void;
}) => {
	const [jsonError, setJsonError] = useState<string | null>(null);
	const fallback = prop.defaultValue || '{}';
	const [rawText, setRawText] = useState(() => serializeJsonValue(value, fallback));

	// Track what we last emitted so we can detect external changes (e.g. Reset All)
	const lastEmittedRef = useRef<string>(JSON.stringify(value));

	useEffect(() => {
		const serialized = JSON.stringify(value);
		if (serialized !== lastEmittedRef.current) {
			lastEmittedRef.current = serialized;
			setRawText(serializeJsonValue(value, fallback));
			setJsonError(null);
		}
	}, [value, fallback]);

	return (
		<S.JsonInputWrapper>
			<S.JsonTextarea
				value={rawText}
				$hasError={!!jsonError}
				onChange={(e) => {
					const newText = e.target.value;
					setRawText(newText);
					try {
						const parsed = JSON.parse(newText);
						setJsonError(null);
						lastEmittedRef.current = JSON.stringify(parsed);
						onChange(parsed);
					} catch {
						setJsonError('Invalid JSON');
					}
				}}
				placeholder={fallback}
			/>
			{jsonError && <S.JsonError>{jsonError}</S.JsonError>}
		</S.JsonInputWrapper>
	);
};

const ComponentPage = ({ component, rawCode, showBackButton }: ComponentPageProps) => {
	const dispatch = useDispatch();
	const tabId = `components-${component.id}`;
	const scrollRef = useScrollMemory(tabId);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingSaveRef = useRef<(() => void) | null>(null);
	const { dsTabs, selectedDsPicks, iconPack, activeDesignSystemId, designSystemContent, dsChat } =
		useSelector(
			(r: RootReducer) => ({
				dsTabs: r.mode.dsTabs,
				selectedDsPicks: r.mode.selectedDsPicks,
				iconPack: r.mode.designSystemContent?.config?.iconPack ?? DEFAULT_ICON_PACK,
				activeDesignSystemId: r.mode.activeDesignSystemId,
				designSystemContent: r.mode.designSystemContent,
				dsChat: r.db.activeDsChat,
			}),
			shallowEqual
		);

	const isComponentSelected = selectedDsPicks.some(
		(p) => p.kind === 'component' && p.tagName === component.tagName
	);

	// Initialize default props from component API defaults
	const apiDefaults = useMemo(() => {
		const defaults: Record<string, any> = {};
		component.props.forEach((prop) => {
			if (prop.defaultValue !== undefined) {
				if (prop.type === 'json') {
					try {
						defaults[prop.name] = JSON.parse(prop.defaultValue);
					} catch {
						defaults[prop.name] = prop.defaultValue;
					}
				} else if (prop.type === 'boolean') {
					defaults[prop.name] = prop.defaultValue === 'true';
				} else if (prop.type === 'number') {
					defaults[prop.name] = Number(prop.defaultValue);
				} else {
					defaults[prop.name] = prop.defaultValue;
				}
			}
		});
		return defaults;
	}, [component.props]);

	// Overlay example props on top of API defaults (example wins)
	const initialProps = useMemo(() => {
		return { ...apiDefaults, ...(component.exampleProps || {}) };
	}, [apiDefaults, component.exampleProps]);

	// User overrides layered on top of defaults; empty = all defaults
	const [overrides, setOverrides] = useState<Record<string, any>>({});
	const [overrideChildren, setChildrenOverride] = useState<string | null>(null);
	const [codeTab, setCodeTab] = useState<'guidelines' | 'js' | 'css' | 'examples' | 'dependencies'>(
		() => (component.guidelines ? 'guidelines' : 'js')
	);

	// Reset overrides and tab default when navigating to a different component
	useEffect(() => {
		setOverrides({});
		setChildrenOverride(null);
		setCodeTab(component.guidelines ? 'guidelines' : 'js');
	}, [component.id, component.guidelines]);

	// Flush any pending save on unmount
	useEffect(() => {
		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			pendingSaveRef.current?.();
		};
	}, []);

	// Derive guidelines file path: ds-button -> components/button/button.guidelines.md
	const guidelinesPath = useMemo(() => {
		const name = component.tagName.replace(/^ds-/, '');
		return `components/${name}/${name}.guidelines.md`;
	}, [component.tagName]);

	const onGuidelinesChange = useCallback(
		(markdown: string) => {
			if (!activeDesignSystemId) return;
			if (markdown === component.guidelines) return;
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			// Fires on a debounce as the user types; failures are silent since
			// the next keystroke will retry. Surfacing toasts here would spam the user.
			const save = () => {
				pendingSaveRef.current = null;
				forge.saveDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path: guidelinesPath, content: markdown }],
				});
			};
			pendingSaveRef.current = save;
			saveTimerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
		},
		[activeDesignSystemId, guidelinesPath, component.guidelines]
	);

	// Merge defaults with user overrides — defaults update from Redux without clobbering overrides
	const mergedProps = useMemo(() => ({ ...initialProps, ...overrides }), [initialProps, overrides]);
	const displayChildren = overrideChildren ?? component.children ?? '';

	const dependencies = useMemo(() => {
		const graph = designSystemContent?.graph;
		if (!graph) return { theme: [], styles: [], components: [], hasBroken: false };

		const deps = [...new Set(graph.uses[component.tagName] || [])];
		const symbols = graph.symbols;
		const defined = new Set(Object.keys(symbols));

		const theme: { sym: string; broken: boolean }[] = [];
		const styles: { sym: string; broken: boolean }[] = [];
		const components: { sym: string; broken: boolean }[] = [];

		for (const dep of deps) {
			const broken = !defined.has(dep);
			const file = symbols[dep]?.file ?? '';
			if (file.startsWith('theme/') || dep.startsWith('--theme-')) {
				theme.push({ sym: dep, broken });
			} else if (file.startsWith('styles/') || symbols[dep]?.kind === 'class') {
				styles.push({ sym: dep, broken });
			} else if (file.startsWith('components/') || symbols[dep]?.kind === 'component') {
				components.push({ sym: dep, broken });
			} else if (broken) {
				// broken refs with no symbol info — guess from prefix
				if (dep.startsWith('--theme-')) theme.push({ sym: dep, broken });
				else if (dep.startsWith('--ds-')) styles.push({ sym: dep, broken });
				else if (dep.startsWith('ds-')) components.push({ sym: dep, broken });
				else styles.push({ sym: dep, broken });
			}
		}

		// sort: healthy first, broken at end
		const sort = (a: { broken: boolean }, b: { broken: boolean }) => +a.broken - +b.broken;
		theme.sort(sort);
		styles.sort(sort);
		components.sort(sort);

		const hasBroken =
			theme.some((d) => d.broken) ||
			styles.some((d) => d.broken) ||
			components.some((d) => d.broken);
		return { theme, styles, components, hasBroken };
	}, [designSystemContent?.graph, component.tagName]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDepClick = (sym: string, section: 'theme' | 'styles' | 'components') => {
		const chrome = DS_TAB_CHROME[section];
		const contentPath = section === 'components' ? sym.replace(/^ds-/, '') : undefined;
		const tabId = contentPath ? `${section}-${contentPath}` : `section-${section}`;
		const existingTab = dsTabs.find((t) => t.id === tabId);
		if (existingTab) {
			dispatch(setActiveDsTab(tabId));
		} else {
			dispatch(addDsTab({ id: tabId, section, contentPath, label: chrome.label }));
		}
	};

	const onAutofix = async () => {
		if (!dsChat) return;
		const brokenDeps = [
			...dependencies.theme.filter((d) => d.broken),
			...dependencies.styles.filter((d) => d.broken),
			...dependencies.components.filter((d) => d.broken),
		];
		if (brokenDeps.length === 0) return;

		const lines = [
			`The component \`${component.tagName}\` has broken dependencies. Please update it to fix these references:`,
			...brokenDeps.map((d) => `- \`${d.sym}\` is no longer defined`),
		];

		forge
			.updateDsChat({
				update: {
					id: dsChat.id,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: '' },
				},
			})
			.catch((e) => console.error('Failed to update chat status:', e));

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message: lines.join('\n'),
			attachments: [],
		};
		await forge.dsChatAddUserEntry.withToast()({
			dsChatId: dsChat.id,
			entry,
			chatMode: ChatMode.DESIGN_AND_CHAT,
		});

		dispatch(resolveBreakageSymbols(brokenDeps.map((d) => d.sym)));
	};

	const onPropChange = (propName: string, value: any) => {
		setOverrides((prev) => ({ ...prev, [propName]: value }));
	};

	const onResetProps = () => {
		setOverrides({});
		setChildrenOverride(null);
	};

	const onCopyCode = () => {
		if (codeTab === 'guidelines' || codeTab === 'dependencies') return;
		let code: string;
		if (codeTab === 'js') {
			code = component.sourceCode || '// No source code available';
		} else if (codeTab === 'css') {
			code = component.cssCode || '/* No CSS found for this component */';
		} else {
			code = buildExampleHtml(component, mergedProps, displayChildren);
		}
		navigator.clipboard.writeText(code);
		toast.success('Copied to clipboard');
	};

	const onBackToList = () => {
		// Navigate to the main Components tab if it exists, otherwise create it
		const componentsTab = dsTabs.find((tab) => tab.section === 'components' && !tab.contentPath);
		if (componentsTab) {
			dispatch(setActiveDsTab(componentsTab.id));
		} else {
			// Create a new Components tab
			const newTabId = 'section-components';
			const chrome = DS_TAB_CHROME.components;
			dispatch(
				addDsTab({
					id: newTabId,
					section: 'components',
					label: chrome?.label || 'Components',
				})
			);
			dispatch(setActiveDsTab(newTabId));
		}
	};

	const onDelete = () => {
		if (!activeDesignSystemId || designSystemEditingBlocked()) return;
		const name = component.name;
		const tagName = component.tagName;
		const componentName = tagName.replace(/^ds-/, '');
		displayConfirmModal({
			title: 'Delete component',
			description: `Are you sure you want to delete "${name}"?`,
			submitLabel: 'Delete',
			onCancel: () => {},
			onSubmit: async () => {
				if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
				saveTimerRef.current = null;
				pendingSaveRef.current = null;
				await forge.updateDsFiles({
					designSystemId: activeDesignSystemId,
					files: [
						{ path: `components/${componentName}/${componentName}.js`, content: '' },
						{ path: `components/${componentName}/${componentName}.css`, content: '' },
						{ path: `components/${componentName}/${componentName}.example.json`, content: '' },
						{ path: `components/${componentName}/${componentName}.guidelines.md`, content: '' },
					],
				});
				dispatch(removeDsTab(tabId));
				dispatch(removeDsPick({ kind: 'component', tagName }));
			},
		});
	};

	const onContextMenuInCode = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderPropControl = (prop: ComponentProp) => {
		const value = mergedProps[prop.name];

		// Select type: use dropdown for >5 options, pill buttons otherwise
		if (prop.type === 'select' && prop.options && prop.options.length > 0) {
			if (prop.options.length > 5) {
				return (
					<S.PropSelect
						value={value || prop.defaultValue || ''}
						onChange={(e) => onPropChange(prop.name, e.target.value)}
					>
						{prop.options.map((opt) => (
							<option key={opt} value={opt}>
								{opt}
							</option>
						))}
					</S.PropSelect>
				);
			}
			return (
				<S.ControlButtons>
					{prop.options.map((opt) => (
						<S.ControlButton
							key={opt}
							$active={value === opt}
							onClick={() => onPropChange(prop.name, opt)}
						>
							{opt}
						</S.ControlButton>
					))}
				</S.ControlButtons>
			);
		}

		// Boolean type: render toggle buttons
		if (prop.type === 'boolean') {
			return (
				<S.ControlButtons>
					<S.ControlButton $active={!value} onClick={() => onPropChange(prop.name, false)}>
						Off
					</S.ControlButton>
					<S.ControlButton $active={!!value} onClick={() => onPropChange(prop.name, true)}>
						On
					</S.ControlButton>
				</S.ControlButtons>
			);
		}

		// Number type: render number input
		if (prop.type === 'number') {
			return (
				<S.PropTextInput
					type="number"
					value={value !== undefined && value !== null ? value : ''}
					onChange={(e) =>
						onPropChange(prop.name, e.target.value ? Number(e.target.value) : undefined)
					}
					placeholder={prop.defaultValue || '0'}
				/>
			);
		}

		// JSON type: render textarea with validation
		if (prop.type === 'json') {
			return <JsonInput prop={prop} value={value} onChange={(v) => onPropChange(prop.name, v)} />;
		}

		// Icon type: render autocomplete (also fallback for string props named icon/*Icon)
		const isIconProp =
			prop.type === 'icon' || (prop.type === 'string' && /^(icon|.*Icon)$/i.test(prop.name));
		if (isIconProp) {
			return (
				<IconNameInput
					iconPack={iconPack}
					value={value ?? ''}
					onChange={(v) => onPropChange(prop.name, v)}
					placeholder={prop.defaultValue || 'Search icons...'}
				/>
			);
		}

		// String type (default): render text input
		return (
			<S.PropTextInput
				type="text"
				value={value || ''}
				onChange={(e) => onPropChange(prop.name, e.target.value)}
				placeholder={prop.defaultValue || ''}
			/>
		);
	};

	return (
		<S.Base ref={scrollRef}>
			<S.Header>
				<S.HeaderContent>
					{showBackButton && (
						<S.BackButton onClick={onBackToList}>
							<Icon type={IconType.LINE_ARROW_LEFT} width={18} height={18} />
						</S.BackButton>
					)}
					<S.Title>{component.name}</S.Title>
					<S.HeaderActions>
						<S.SelectToggle
							$selected={isComponentSelected}
							onClick={() =>
								dispatch(
									toggleDsPick({
										kind: 'component',
										tagName: component.tagName,
									})
								)
							}
						>
							{isComponentSelected ? 'Added to Chat' : 'Add to Chat'}
						</S.SelectToggle>
						{!designSystemEditingBlocked() && (
							<S.DeleteButton onClick={onDelete}>Delete</S.DeleteButton>
						)}
					</S.HeaderActions>
				</S.HeaderContent>
			</S.Header>

			<S.Content>
				<S.PreviewSection>
					{component.props.length > 0 && (
						<S.PropsContainer>
							<S.PropsHeader>
								<S.ResetButton onClick={onResetProps}>Reset All</S.ResetButton>
							</S.PropsHeader>
							<S.PropsControls>
								{component.props.map((prop) => (
									<S.ControlGroup key={prop.name}>
										<S.ControlLabel>
											{prop.name}
											{prop.required && <S.RequiredMarker>*</S.RequiredMarker>}
										</S.ControlLabel>
										{renderPropControl(prop)}
									</S.ControlGroup>
								))}
								{component.children && (
									<S.ControlGroup style={{ width: '100%' }}>
										<S.ControlLabel>children</S.ControlLabel>
										<S.JsonTextarea
											value={displayChildren}
											onChange={(e) => setChildrenOverride(e.target.value)}
											placeholder="<ds-avatar></ds-avatar>"
											style={{ minWidth: '100%', fontFamily: 'monospace' }}
										/>
									</S.ControlGroup>
								)}
							</S.PropsControls>
						</S.PropsContainer>
					)}

					<S.PreviewWrapper>
						<ComponentPreview
							component={component}
							props={mergedProps}
							children={displayChildren}
							rawCode={rawCode}
						/>
					</S.PreviewWrapper>
				</S.PreviewSection>

				<S.DocumentationSection>
					<S.CodeBlock>
						<S.CodeHeader>
							<S.CodeTabs>
								{component.guidelines && (
									<S.CodeTab
										$active={codeTab === 'guidelines'}
										onClick={() => setCodeTab('guidelines')}
									>
										Guidelines
									</S.CodeTab>
								)}
								<S.CodeTab $active={codeTab === 'js'} onClick={() => setCodeTab('js')}>
									JavaScript
								</S.CodeTab>
								<S.CodeTab $active={codeTab === 'css'} onClick={() => setCodeTab('css')}>
									CSS
								</S.CodeTab>
								<S.CodeTab $active={codeTab === 'examples'} onClick={() => setCodeTab('examples')}>
									Examples
								</S.CodeTab>
								<S.CodeTab
									$active={codeTab === 'dependencies'}
									onClick={() => setCodeTab('dependencies')}
								>
									Dependencies
									{dependencies.hasBroken && (
										<S.BrokenBadge>
											{
												[
													...dependencies.theme,
													...dependencies.styles,
													...dependencies.components,
												].filter((d) => d.broken).length
											}
										</S.BrokenBadge>
									)}
								</S.CodeTab>
							</S.CodeTabs>
							{codeTab !== 'guidelines' && codeTab !== 'dependencies' && (
								<S.CodeCopyButton onClick={onCopyCode}>Copy</S.CodeCopyButton>
							)}
						</S.CodeHeader>
						<S.CodeContent onContextMenu={onContextMenuInCode}>
							{codeTab === 'guidelines' ? (
								<MarkdownEditor
									key={guidelinesPath}
									content={component.guidelines!}
									editable={!designSystemEditingBlocked()}
									onChange={onGuidelinesChange}
								/>
							) : codeTab === 'dependencies' ? (
								<S.DepsContent>
									{dependencies.theme.length > 0 && (
										<S.DepsGroup>
											<S.DepsGroupLabel>Theme</S.DepsGroupLabel>
											<S.DepsPills>
												{dependencies.theme.map(({ sym, broken }) => (
													<Pill
														key={sym}
														label={symbolDisplayName(sym)}
														variant={broken ? 'deleted' : 'default'}
														onClick={broken ? undefined : () => onDepClick(sym, 'theme')}
													/>
												))}
											</S.DepsPills>
										</S.DepsGroup>
									)}
									{dependencies.styles.length > 0 && (
										<S.DepsGroup>
											<S.DepsGroupLabel>Styles</S.DepsGroupLabel>
											<S.DepsPills>
												{dependencies.styles.map(({ sym, broken }) => (
													<Pill
														key={sym}
														label={symbolDisplayName(sym)}
														variant={broken ? 'deleted' : 'default'}
														onClick={broken ? undefined : () => onDepClick(sym, 'styles')}
													/>
												))}
											</S.DepsPills>
										</S.DepsGroup>
									)}
									{dependencies.components.length > 0 && (
										<S.DepsGroup>
											<S.DepsGroupLabel>Components</S.DepsGroupLabel>
											<S.DepsPills>
												{dependencies.components.map(({ sym, broken }) => (
													<Pill
														key={sym}
														label={symbolDisplayName(sym)}
														variant={broken ? 'deleted' : 'default'}
														onClick={broken ? undefined : () => onDepClick(sym, 'components')}
													/>
												))}
											</S.DepsPills>
										</S.DepsGroup>
									)}
									{dependencies.theme.length === 0 &&
										dependencies.styles.length === 0 &&
										dependencies.components.length === 0 && (
											<S.DepsEmpty>No dependencies detected</S.DepsEmpty>
										)}
									{dependencies.hasBroken && dsChat && (
										<S.DepsAutofix>
											<S.AutofixButton
												onClick={onAutofix}
												disabled={dsChat.metadata?.status === ChatStatus.PROCESSING}
												data-tooltip-id="deps-autofix-tip"
												data-tooltip-content="Ask Moonchild to fix items affected by what you removed."
											>
												Autofix
											</S.AutofixButton>
											<Tooltip id="deps-autofix-tip" placement="top" />
										</S.DepsAutofix>
									)}
								</S.DepsContent>
							) : (
								<pre>
									{codeTab === 'js'
										? component.sourceCode || '// No source code available'
										: codeTab === 'css'
										? component.cssCode || '/* No CSS found for this component */'
										: buildExampleHtml(component, mergedProps, displayChildren)}
								</pre>
							)}
						</S.CodeContent>
					</S.CodeBlock>
				</S.DocumentationSection>
			</S.Content>
		</S.Base>
	);
};

export default ComponentPage;
